"use strict";

exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _Checkbox = _interopRequireDefault(require("./Checkbox"));
var _Input = _interopRequireDefault(require("../Input/Input"));
var _Button = _interopRequireDefault(require("../Button/Button"));
var _Dropdown = _interopRequireDefault(require("../Dropdown/Dropdown"));
var _useFormElementValidation = _interopRequireDefault(require("../../hooks/useFormElementValidation"));
var _useRandomId = _interopRequireDefault(require("../../hooks/useRandomId"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
/**
 * The CheckboxGroup component offers a convenient way to display multiple Checkbox components
 */
var CheckboxMultiple = function CheckboxMultiple(_ref) {
  var options = _ref.options,
    checked = _ref.checked,
    controlledId = _ref.id,
    controlledName = _ref.name,
    inputLabel = _ref.inputLabel,
    _ref$inputSelectionLa = _ref.inputSelectionLabel,
    inputSelectionLabel = _ref$inputSelectionLa === void 0 ? 'selections' : _ref$inputSelectionLa,
    searchLabel = _ref.searchLabel,
    selectButtonLabel = _ref.selectButtonLabel,
    clearButtonLabel = _ref.clearButtonLabel,
    helper = _ref.helper,
    inline = _ref.inline,
    displayAsButton = _ref.displayAsButton,
    checkboxesClassName = _ref.checkboxesClassName,
    size = _ref.size,
    color = _ref.color,
    outline = _ref.outline,
    required = _ref.required,
    placeholder = _ref.placeholder,
    atLeast = _ref.atLeast,
    atMost = _ref.atMost,
    onChange = _ref.onChange,
    onError = _ref.onError,
    className = _ref.className,
    _ref$displaySearchBar = _ref.displaySearchBar,
    displaySearchBar = _ref$displaySearchBar === void 0 ? false : _ref$displaySearchBar,
    customMessage = _ref.customMessage;
  var id = _react.default.useRef((0, _useRandomId.default)(controlledId)).current;
  var name = controlledName || id;
  var _React$useState = _react.default.useState(checked || []),
    stateChecked = _React$useState[0],
    setChecked = _React$useState[1];
  var _React$useState2 = _react.default.useState(''),
    searchTerms = _React$useState2[0],
    setSearchTerms = _React$useState2[1];
  var _React$useState3 = _react.default.useState(options || []),
    stateOptions = _React$useState3[0],
    setOptions = _React$useState3[1];
  var _React$useState4 = _react.default.useState(false),
    error = _React$useState4[0],
    setError = _React$useState4[1];
  var _useFormElementValida = (0, _useFormElementValidation.default)(stateChecked, {
      required: required,
      atLeast: atLeast,
      atMost: atMost,
      customMessage: customMessage
    }),
    errors = _useFormElementValida[0],
    methods = _useFormElementValida[1];
  _react.default.useEffect(function () {
    return setChecked(checked || []);
  }, [checked]);
  _react.default.useEffect(function () {
    setError(errors.length > 0);
    if (typeof onError === 'function') {
      onError({
        name: name,
        errors: errors.length ? errors : []
      });
    }
  }, [errors, onError, name]);
  _react.default.useEffect(function () {
    var newOptions = options.filter(function (option) {
      return option.label.toLowerCase().includes(searchTerms.toLowerCase());
    });
    setOptions(newOptions);
  }, [options, searchTerms]);
  var handleChange = function handleChange(checkedCheckbox, ref, event) {
    var checkboxId = event.target.id;
    var newStateChecked = stateChecked;
    if (checkedCheckbox) {
      if (atMost && stateChecked && stateChecked.length === atMost) {
        newStateChecked.shift();
      }
      newStateChecked.push(checkboxId);
    } else {
      newStateChecked = stateChecked.filter(function (value) {
        return value !== checkboxId;
      });
    }
    setChecked(newStateChecked);
    methods.onChangeCheckboxGroup(newStateChecked);
    if (typeof onChange === 'function') {
      onChange(newStateChecked, name, event);
    }
  };
  var handleBlur = function handleBlur() {
    return methods.onChangeCheckboxGroup(stateChecked);
  };
  var classes = (0, _clsx.default)('uk-checkbox-group', (helper.length > 0 || error) && 'has-helper', error && 'has-error', inline && 'checkbox-group--inline', className);
  var handleSelectAll = function handleSelectAll() {
    var newStateChecked = options.filter(function (option) {
      return !option.disabled;
    }).map(function (option) {
      return option.id;
    });
    setChecked(newStateChecked);
    methods.onChangeCheckboxGroup(newStateChecked);
    if (typeof onChange === 'function') {
      onChange(newStateChecked);
    }
  };
  var handleUnselectAll = function handleUnselectAll() {
    if (stateChecked.length > 0) {
      setChecked([]);
      methods.onChangeCheckboxGroup([]);
      if (typeof onChange === 'function') {
        onChange([]);
      }
    }
  };
  var customDisplay = function customDisplay() {
    if (stateChecked.length) {
      if (stateChecked.length > 1) {
        return stateChecked.length + " " + inputSelectionLabel;
      }
      var opt = options.find(function (option) {
        return String(option.id) === String(stateChecked[0]) || String(option.key) === String(stateChecked[0]);
      });
      return opt ? "" + opt.label : '';
    }
    return '';
  };
  var getIsSelectAllDisabled = function getIsSelectAllDisabled() {
    if (atMost === 0 || atMost && options && atMost < options.length) {
      return true;
    }
    if (options && stateChecked) {
      return options.length === stateChecked.length;
    }
    return false;
  };
  return /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-checkbox-multiple"
  }, /*#__PURE__*/_react.default.createElement(_Dropdown.default, {
    tabIndex: "-1",
    withIcon: false,
    trigger: /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_Input.default, {
      disabled: !options || !options.length,
      placeholder: placeholder,
      readOnly: true,
      tabIndex: "0",
      className: stateChecked && stateChecked.length > 0 ? 'value-selected' : 'no-value-selected',
      value: customDisplay(),
      label: inputLabel,
      onChange: handleUnselectAll,
      "aria-label": "Display multiple checkboxes",
      "aria-describedby": (name || id) + "-helper-text",
      id: id,
      icon: "wq-filter-entonoir"
    }))
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "width-100"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "grid-x grid-margin-x margin-bottom-1"
  }, /*#__PURE__*/_react.default.createElement(_Button.default, {
    onClick: handleSelectAll,
    displayAsBlock: true,
    className: "cell auto select-all",
    color: "secondary",
    label: selectButtonLabel,
    disabled: getIsSelectAllDisabled(),
    size: "small"
  }), /*#__PURE__*/_react.default.createElement(_Button.default, {
    onClick: handleUnselectAll,
    displayAsBlock: true,
    className: "cell auto unselect-all",
    color: "secondary",
    label: clearButtonLabel,
    disabled: !stateChecked.length,
    size: "small"
  })), displaySearchBar && /*#__PURE__*/_react.default.createElement("div", {
    className: ""
  }, /*#__PURE__*/_react.default.createElement(_Input.default, {
    className: "input-search",
    onChange: function onChange(searchValue) {
      return setSearchTerms(searchValue);
    },
    clearable: true,
    placeholder: searchLabel,
    icon: "search",
    "aria-label": "Search"
  })), /*#__PURE__*/_react.default.createElement("div", {
    className: "checkbox-list-container"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "grid-y " + classes
  }, stateOptions.map(function (item, i) {
    return /*#__PURE__*/_react.default.createElement(_Checkbox.default, {
      key: item.id || id + "_" + i,
      name: item.name || name + "_" + i,
      id: item.id || id + "_" + i,
      label: item.label,
      checked: stateChecked.includes(item.id) || stateChecked.includes(id + "_" + i),
      disabled: item.disabled || false,
      onChange: handleChange,
      onBlur: handleBlur,
      color: color,
      size: size,
      outline: outline,
      value: item.value,
      className: checkboxesClassName,
      displayAsButton: displayAsButton
    });
  }), (helper.length > 0 || error) && /*#__PURE__*/_react.default.createElement("small", {
    className: "helper",
    id: (name || id) + "-helper-text"
  }, error ? errors : helper), atMost && atMost === 1 && /*#__PURE__*/_react.default.createElement("small", {
    className: "helper"
  }, "maximum 1 option"), atMost && atMost > 1 && /*#__PURE__*/_react.default.createElement("small", {
    className: "helper"
  }, "maximum " + atMost + " options"))))));
};
CheckboxMultiple.propTypes = {
  /**
   * Array of Checkboxes props
   */
  options: _propTypes.default.arrayOf(_propTypes.default.shape),
  /**
   * Ids of the Checkboxes to set as selected
   */
  checked: _propTypes.default.arrayOf(_propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.number])),
  /**
   * Prefix for the Checkboxes id (unless it has been set in the options prop)
   */
  id: _propTypes.default.string,
  /**
   * Prefix for the Checkboxes name (unless it has been set in the options prop)
   */
  name: _propTypes.default.string,
  /**
   * Label of the CheckboxeGroup
   */
  inputLabel: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  inputSelectionLabel: _propTypes.default.string,
  /**
   * Label of the Search-bar
   */
  searchLabel: _propTypes.default.string,
  /**
   * Label of select all button
   */
  selectButtonLabel: _propTypes.default.string,
  /**
   * Label of clear button
   */
  clearButtonLabel: _propTypes.default.string,
  /**
   * Layout of the CheckboxeGroup
   */
  inline: _propTypes.default.bool,
  /**
   * Change the display of the CheckboxeGroup
   */
  displayAsButton: _propTypes.default.bool,
  /**
   * ClassName to apply to each Checkbox
   */
  checkboxesClassName: _propTypes.default.string,
  placeholder: _propTypes.default.string,
  /**
   * Size of the Checkboxes
   */
  size: _propTypes.default.oneOf(['tiny', 'small', 'default', 'large', 'big']),
  /**
   * Background-color of selected Checkboxes (or checkmark color if outline)
   */
  color: _propTypes.default.oneOf(['default', 'primary', 'secondary', 'success', 'alert', 'warning', 'white']),
  /**
   * Invert styling colors of the Checkboxes
   */
  outline: _propTypes.default.bool,
  /**
   * Helper text, is replaced by a message in case of error
   */
  helper: _propTypes.default.string,
  /**
   * Form-validation: set the CheckboxGroup as required
   */
  required: _propTypes.default.bool,
  /**
   * Form-validation: set the minimal number of Checkboxes that must be checked
   */
  atLeast: _propTypes.default.number,
  /**
   * Form-validation: set the maximal number of Checkboxes that can be checked
   */
  atMost: _propTypes.default.number,
  /**
   * Callback function onChange
   * Only handle when onClickClear (clearable: handleOnClear) beacause Input is in readOnly mode
   */
  onChange: _propTypes.default.func,
  /**
   * Callback function onError
   */
  onError: _propTypes.default.func,
  /**
   * Add a custom message when custom validation is used
   */
  customMessage: _propTypes.default.object
};
CheckboxMultiple.defaultProps = {
  inputLabel: '',
  searchLabel: 'Search',
  selectButtonLabel: 'Select All',
  clearButtonLabel: 'Clear All',
  size: 'default',
  color: 'default',
  outline: false,
  required: false,
  helper: '',
  checkboxesClassName: '',
  displayAsButton: false
};
var _default = CheckboxMultiple;
exports.default = _default;